import { Button } from "../../../Components/UI/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../Components/UI/card";
import { useCurrency } from "../../../Hooks/useCurrency";
import { Award, Plus, Trophy, ChevronDown, ChevronUp } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import UserEntry from "../../Users/UserEntry";
import ThreadEntry from "../../Threads/ThreadEntry";
import { useSelector } from "react-redux";
import { checkIfInqOnlyMode } from "../../../Store/Reducers";
import { checkUserRights, FormatAmount, formatAmount } from "../../../Utils/Utilities";
import { getAgencyData } from "../../../Utils/Storage";
import useIsMobile from "../../../Hooks/useIsMobile";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const Leaderboard = ({ leaderboardData = {}, isAdmin = false, isOrderVisible = true }) => {
  const navigate = useNavigate();
  const currencySymbol = useCurrency();
  const isInqOnly = useSelector(checkIfInqOnlyMode);
  const agencyData = getAgencyData();
  const isMobile = useIsMobile();
  const { data: activeData } = useSelector(state => state.activeData);

  // For mobile view, show only first 5 entries unless expanded
  const [showAll, setShowAll] = useState(false);
  const [maximumDecimal, setMaximumDecimal] = useState(0);
  const [dialogStates, setDialogStates] = useState({
    userEntry: false,
    threadEntry: false,
  });
  const mobileDisplayedData = showAll ? leaderboardData : leaderboardData.slice(0, 5);
  const userRights = checkUserRights();

  // For mobile view, show only first 5 entries unless expanded
  const hasMore = leaderboardData.length > 5;

  const getDecimalPlaces = (num) => {
	if (num == null) return 0;
		const str = num.toString();
			return str.includes('.') ? str.split('.')[1].length : 0;
	};

	useEffect(() => {
		if (!Array.isArray(leaderboardData) || leaderboardData.length === 0) {
			setMaximumDecimal(0);
			return;
		}

		const maxDecimals = leaderboardData.reduce((max, item) => {
			const decimals = getDecimalPlaces(item.price);
			return Math.max(max, decimals);
		}, 0);

		setMaximumDecimal(maxDecimals);
	}, [leaderboardData]);


  // Calculate total orders count and value
  const orderStats = useMemo(() => {
    const nowDT = new Date();
    const currMonthNumber = nowDT.getMonth() + 1;

    const monthlySOs = JSON.parse(activeData?.MonthlyThreads || "[]").filter(
      m => m.TID === 2 && m.MM === currMonthNumber
    );

    const orderCounts = monthlySOs.length
      ? Math.abs(monthlySOs.reduce((sum, item) => sum + (item.Rcvd || 0), 0))
      : 0;
    const orderValues = monthlySOs.length
      ? Math.abs(monthlySOs.reduce((sum, item) => sum + (item.Amt || 0), 0))
      : 0;

    return { orderCounts, orderValues };
  }, [activeData]);

  const handleOpenConfig = (configType) => {
    if (configType === 'userEntry') {
      setDialogStates((prev) => ({ ...prev, userEntry: true }));
      return;
    }
    if (configType === 'threadEntry') {
      setDialogStates((prev) => ({ ...prev, threadEntry: true }));
      return;
    }
  };
  const currentMonth = new Date().getMonth() + 1;

  const handleUserClick = (person) => {
    const url = `/threads-report?tID=2&m=${currentMonth}&eID=${person.id}`;

    if (isMobile) {
      // Mobile: navigate within app
      navigate(url);
    } else {
      // Desktop: open in new tab
      window.open(`/Web${url}`, '_blank');
    }
  };

  const isInqOnlyAndMobile = isInqOnly && isMobile;

  const getAvatarContent = (person, index) => {
    return index + 1;
  };

  const currMonth = format(new Date(), "MMM");

  return (
    <>
      <Card
        className={`flex flex-col overflow-hidden bg-sahi-white md:shadow-md ${isMobile ? '!pt-0' : '!pt-3'} !rounded-xl border-none shadow-sm gap-0 md:gap-3 cursor-pointer hover:border-slate-300 transition-all`}
        onClick={() => !dialogStates.threadEntry && !dialogStates.userEntry && navigate(`/threads-report?tID=2&m=${currentMonth}`)}
      >
      {/* Desktop Header - hidden on mobile */}
      <CardHeader className="sm:pt-0 pt-3 px-5 flex-shrink-0 hidden md:block">
        <CardTitle className="flex items-center gap-2 text-lg justify-between">
          <div className="flex items-center">
            {/* <Trophy className="w-5 h-5 text-sahi-green" /> */}
            <span className="text-text-color text-lg sm:text-xl font-semibold">{isInqOnly ? 'Team Performance' : 'Leaderboard'}</span>


            {isInqOnly && userRights?.addThr && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenConfig('threadEntry');
                }}
                className="h-8 w-8 ml-1 flex items-center justify-center sahi-radius hover:bg-gray-100 transition-colors text-sahi-primary-color"
              >
                <Plus className="h-6 w-6" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 ml-auto">

            <div className="text-right flex items-baseline gap-1">
              <div className="text-xl font-bold text-sahi-dark-blue flex items-baseline gap-1">
                <span className="text-lg text-sahi-grey">{currencySymbol}</span>
                {orderStats.orderValues > 0 ? (
                  <FormatAmount amount={orderStats.orderValues} countryID={agencyData.CountryID} showCurr={false}/>
                ) : (
                  <span>0</span>
                )}
                <span className="text-base font-semibold text-sahi-grey">
                  ({orderStats.orderCounts.toLocaleString()})
                </span>
              </div>
            </div>

            {isAdmin && !isInqOnly &&
              <div
                className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
                onClick={() => handleOpenConfig(`userEntry`)}
              >
                <Plus className="h-6 w-6" />
              </div>
            }
          </div>
        </CardTitle>
      </CardHeader>

      {/* Mobile Header with reduced padding */}
      <div className="md:hidden flex items-center justify-between p-3 px-3 pb-1">
        <div className="flex items-center">
          <span className="text-text-color text-lg sm:text-xl font-semibold sahi-radius">
            {isInqOnly ? 'Team Performance' : 'Team Performance'}
          </span>

          {/* FIX: ADD THIS BLOCK FOR ORDERS IN MOBILE */}
          {/* {isInqOnly && userRights?.addThr && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenConfig('threadEntry');
              }}
              className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
            >
              <Plus className="h-6 w-6" />
            </button>
          )} */}

          {/* Existing logic for Leaderboard User Addition */}
          {!isInqOnly ? (
            isAdmin && (
              <div
                className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenConfig("userEntry");
                }}
              >
                <Plus className="h-6 w-6" />
              </div>
            )
          ) : null}
        </div>

        <div className="flex items-center ml-auto">

          <div className="text-right flex items-baseline gap-1">
            <div className="text-lg font-bold text-sahi-dark-blue flex items-baseline gap-1">
              <span className="text-base text-sahi-grey">{currencySymbol}</span>
              {orderStats.orderValues > 0 ? (
                <span>{formatAmount(orderStats.orderValues, agencyData.CountryID)}</span>
              ) : (
                <span>0</span>
              )}
              <span className="text-sm font-semibold text-sahi-grey">
                ({orderStats.orderCounts.toLocaleString()})
              </span>
            </div>
          </div>

          {!isInqOnly ? (
            isAdmin && (
              <div
                className="p-1 text-sahi-primary-color hover:cursor-pointer hover:bg-sahi-blue-light sahi-radius duration-200"
                onClick={() => handleOpenConfig("userEntry")}
              >
                <Plus className="h-6 w-6" />
              </div>
            )
          ) : null}
        </div>
      </div>

      <CardContent className="md:px-5 md:pb-3 pb-3  px-3  m-0">        
	  <div className="space-y-2 md:flex-1">
        {/* Desktop shows all data, mobile shows limited data */}
        <div className="hidden md:block ">
          {leaderboardData.map((person, index) => (
            <div
              key={person.id}
              onClick={(e) => {
                e.stopPropagation();
                handleUserClick(person);
              }}
              className={`flex items-center gap-2 md:p-2 md:px-3 p-1 px-3 rounded-lg border border-sahi-border-color md:mx-0 bg-sahi-disable mb-2 shadow-sm hover:cursor-pointer`}
            >
              {/* Ranking Badge */}
              <div className="flex-shrink-0">
                <div className={`w-4 h-4 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-sm bg-sahi-primary-color text-sahi-white`}>
                  {getAvatarContent(person, index)}
                </div>
              </div>

              {/* Person Info */}
              <div className="flex-1 min-w-0">
                <div
                  className={`flex items-center font-semibold text-sm sm:text-base leading-tight`}
                  style={{
                    color: index === 0
                      ? "var(--sahi-gold)"
                      : index === 1
                        ? "var(--sahi-silver)"
                        : index === 2
                          ? "var(--sahi-bronze)"
                          : "var(--sahi-grey)"
                  }}
                >
                  <span className="text-foreground truncate">
                    {person.name}
                  </span>
                  {(index < 3 && person.orders > 0 && leaderboardData.length > 1) && <Award className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="text-right flex-shrink-0">
                <div className="font-bold text-sm sm:text-lg leading-tight text-sahi-primary-color flex items-baseline gap-1">
                  <span>
                    {!isMobile && person.price != 0 ? (
                      <FormatAmount amount={person.price} countryID={agencyData.CountryID} prefix={currencySymbol} showDecimalPoints={maximumDecimal} />
                    ) : (
                      <span>{currencySymbol}{formatAmount(person.price, agencyData.CountryID)}</span>
                    )}
                  </span>
                  {isOrderVisible && <span className="text-base font-medium">({person.orders})</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile shows limited data */}
        <div className="md:hidden space-y-2">
          {mobileDisplayedData.map((person, index) => (
            <div
              key={person.id}
              onClick={(e) => {
                e.stopPropagation();
                handleUserClick(person);
              }}
              className={`flex items-center gap-2 md:p-3 md:px-3 p-2 px-3 sahi-radius border border-sahi-border-color md:mx-0 bg-sahi-disable shadow-sm hover:cursor-pointer`}
            >
              {/* Ranking Badge */}
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-sm bg-sahi-primary-color text-sahi-white`}>
                  {getAvatarContent(person, index)}
                </div>
              </div>

              {/* Person Info */}
              <div className="flex-1 min-w-0">
                <div
                  className={`flex items-center font-semibold text-sm sm:text-base leading-tight`}
                  style={{
                    color: index === 0
                      ? "var(--sahi-gold)"
                      : index === 1
                        ? "var(--sahi-silver)"
                        : index === 2
                          ? "var(--sahi-bronze)"
                          : "var(--sahi-grey)"
                  }}
                >
                  <span className="text-foreground truncate">
                    {person.name}
                  </span>
                  {(index < 3 && person.orders > 0 && leaderboardData.length > 1) && <Award className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="text-right flex-shrink-0">
                <div className="font-bold text-sm sm:text-lg leading-tight mb-1 text-sahi-primary-color flex items-baseline gap-1">
                  <span>{currencySymbol} {formatAmount(person.price, agencyData.CountryID)}</span>
                  {isOrderVisible && <span className="text-xs font-semibold text-sahi-dark-blue">({person.orders})</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
        {/* Show More Button - Mobile only - Moved to left */}
        {hasMore && (
          <div className="mt-4 flex justify-start md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(!showAll);
              }}
            >
              <span>{showAll ? 'Show Less' : 'Show More'}</span>
              {showAll ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        )}


      </CardContent>
    </Card>

    {/* User entry dialog */}
    <div onClick={(e) => e.stopPropagation()}>
      <UserEntry
        isOpen={dialogStates.userEntry}
        setIsOpen={(open) => setDialogStates((prev) => ({ ...prev, userEntry: open }))}
        userData={null}
      />
    </div>

    <div onClick={(e) => e.stopPropagation()}>
      <ThreadEntry
        triggerButton={false}
        threadTypeID={2}
        open={dialogStates.threadEntry}
        onOpenChange={(open) => {
          setDialogStates((prev) => ({ ...prev, threadEntry: open }));
        }}
        calledFrom={isInqOnly ? 'ORDDONE' : ''}
      />
    </div>
  </>
)
}

export default Leaderboard;